import { conditionKinds } from '@tunasong/schemas'
import type { Condition, Rule } from '@tunasong/schemas'

/** Human phrase for a single condition. Every `Condition['kind']` MUST be handled here — a
 * silently-unhandled kind is exactly how a rule's scope goes unrepresented (GH review finding 1).
 * Typed to return `string`, not `string | undefined`: that makes an incomplete switch over the
 * `Condition['kind']` union a compile error (falling through returns `undefined`, which does not
 * satisfy `string`) instead of a silently-dropped condition when a new kind is added. */
const phraseFor = (condition: Condition): string => {
  switch (condition.kind) {
    case 'type':
      return condition.entityType
    case 'tag':
      return `tagged ${condition.tag}`
    case 'space':
      return `in space ${condition.spaceId}`
  }
}

/**
 * One-phrase summary of what a rule matches, for an index row. Every condition of every kind is
 * represented: the engine ANDs every condition (`evaluateConditions` in
 * `schemas/src/core/evaluate-conditions.ts` is `conditions.every(...)`), so conditions of the same
 * kind join with "and" here too, and different kinds concatenate. Do not join same-kind conditions
 * with "or" -- that states a broader match than the rule actually fires on.
 */
export const summariseConditions = (rule: Rule): string => {
  const parts = conditionKinds
    .map(kind =>
      rule.conditions
        .filter(condition => condition.kind === kind)
        .map(phraseFor)
        .join(' and ')
    )
    .filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : 'anything'
}

/**
 * Display state for a rule. `paused` is deliberately distinct from `off`: the
 * loop guard paused it, the user did not, and the SPEC requires the two read
 * differently so a user is never told they switched something off themselves.
 */
export const ruleState = (rule: Rule): 'paused' | 'on' | 'off' => {
  if (rule.pausedReason) {
    return 'paused'
  }
  return rule.enabled ? 'on' : 'off'
}

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** Relative "last ran" label. `now` is injected so the test is not clock-dependent. */
export const formatLastFired = (iso: string | undefined, now: Date): string => {
  if (!iso) {
    return "Hasn't run yet"
  }
  const elapsed = now.getTime() - new Date(iso).getTime()
  if (elapsed < MINUTE_MS) {
    return 'Just now'
  }
  if (elapsed < HOUR_MS) {
    return `${Math.floor(elapsed / MINUTE_MS)}m ago`
  }
  if (elapsed < DAY_MS) {
    return `${Math.floor(elapsed / HOUR_MS)}h ago`
  }
  return `${Math.floor(elapsed / DAY_MS)}d ago`
}
